import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle, Clock, XCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  unverified:            { color: 'var(--pp-text-3)',  Icon: Shield,      label: 'Not Started',     bg: 'transparent' },
  started:               { color: '#FFB000',           Icon: Clock,       label: 'In Progress',     bg: 'rgba(255,176,0,0.08)' },
  incomplete:            { color: '#FFB000',           Icon: Clock,       label: 'Documents Pending', bg: 'rgba(255,176,0,0.08)' },
  under_review:          { color: '#2979FF',           Icon: Clock,       label: 'Under Review',    bg: 'rgba(41,121,255,0.08)' },
  resubmission_requested:{ color: '#FFB000',           Icon: Clock,       label: 'Resubmission Needed', bg: 'rgba(255,176,0,0.08)' },
  approved:              { color: '#00FF94',           Icon: CheckCircle, label: 'Verified',        bg: 'rgba(0,255,148,0.08)' },
  rejected:              { color: '#FF3333',           Icon: XCircle,     label: 'Declined',        bg: 'rgba(255,51,51,0.08)' },
};

export default function VerificationPage() {
  const navigate  = useNavigate();
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [polling, setPolling]   = useState(false);

  const load = () => {
    api.getVerificationStatus()
      .then(d => setStatus(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Poll for status updates when verification is in progress
  useEffect(() => {
    if (!status) return;
    const inProgress = ['started', 'under_review', 'resubmission_requested'].includes(status.status);
    if (!inProgress) return;
    const interval = setInterval(() => {
      api.getVerificationStatus().then(d => {
        setStatus(d);
        if (d.verified || d.status === 'rejected') clearInterval(interval);
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [status?.status]);

  const handleStartVeriff = async () => {
    setStarting(true);
    try {
      const data = await api.startVeriffSession();
      if (data.session_url) {
        // Open Veriff in the same window — Veriff will redirect back to /verification
        window.location.href = data.session_url;
      } else if (data.pending_admin || data.stay_on_page) {
        // Silent-fail mode: backend recorded the attempt and pinged the
        // admin for manual review. From the user's perspective the click
        // simply does nothing visible — no error, no navigation. The
        // admin will mark them verified manually.
        await api.getVerificationStatus().then(setStatus).catch(() => {});
      }
    } catch (e) {
      // Never show an error to the user during identity verification —
      // they don't need to debug our infra. We swallow the failure and
      // refresh the local status. The backend already created an admin
      // notification for any failed attempt.
      try { await api.getVerificationStatus().then(setStatus).catch(() => {}); } catch (_) {}
    }
    setStarting(false);
  };

  const handleRefresh = async () => {
    setPolling(true);
    await api.getVerificationStatus().then(setStatus).catch(() => {});
    setPolling(false);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  const currentStatus = status?.status || 'unverified';
  const meta          = STATUS_META[currentStatus] || STATUS_META.unverified;
  const StatusIcon    = meta.Icon;
  const isVerified    = status?.verified === true;
  const canStart      = !['approved', 'under_review', 'started'].includes(currentStatus);

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)', position: 'sticky', top: 0, background: 'var(--pp-bg)', zIndex: 20 }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>IDENTITY VERIFICATION</h1>
        <button onClick={handleRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text-3)' }}>
          <RefreshCw size={16} style={{ animation: polling ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Status banner */}
        <div style={{ background: meta.bg, border: `1px solid ${meta.color}40`, padding: 20, marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <StatusIcon size={28} color={meta.color} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: meta.color }}>{meta.label}</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
              {isVerified
                ? 'Your identity has been verified by Veriff. You have full access to all funded account features.'
                : currentStatus === 'under_review' || currentStatus === 'started'
                  ? 'Your documents are being reviewed by Veriff AI. This typically takes a few minutes.'
                  : currentStatus === 'resubmission_requested'
                    ? 'Additional documents or a clearer photo is required. Please start a new verification.'
                    : currentStatus === 'rejected'
                      ? 'Your verification was declined. Please contact support@profit-pulse.pro for assistance.'
                      : 'Complete identity verification to unlock funded account payouts and full platform access.'}
            </p>
          </div>
        </div>

        {/* Why verify */}
        {!isVerified && currentStatus !== 'under_review' && (
          <div style={{ background: 'var(--pp-overlay)', padding: 14, marginBottom: 20, borderLeft: '3px solid var(--pp-border)' }}>
            <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: 'var(--pp-text-2)' }}>Why do we verify?</p>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.7 }}>
              Identity verification is required to receive funded account payouts, comply with financial regulations, and protect your account. The process takes about 2 minutes and is powered by <strong style={{ color: 'var(--pp-accent)' }}>Veriff</strong> — a leading identity verification provider used by banks and fintechs worldwide.
            </p>
          </div>
        )}

        {/* What you'll need */}
        {canStart && (
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
            <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: 'var(--pp-text-2)', letterSpacing: '0.1em' }}>WHAT YOU'LL NEED</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { icon: '🪪', label: 'Government-issued ID', desc: 'Passport, national ID, or driver\'s license' },
                { icon: '🤳', label: 'Selfie', desc: 'A live photo taken during the verification' },
                { icon: '💡', label: 'Good lighting', desc: 'Ensure your face and document are clearly visible' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{item.label}</p>
                    <p style={{ margin: '1px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        {canStart && (
          <button onClick={handleStartVeriff} disabled={starting} className="btn-primary"
            style={{ width: '100%', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 14, marginBottom: 12 }}>
            {starting ? (
              <><div style={{ width: 18, height: 18, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Starting Verification...</>
            ) : (
              <><Shield size={18} />START IDENTITY VERIFICATION<ExternalLink size={14} /></>
            )}
          </button>
        )}

        {currentStatus === 'under_review' || currentStatus === 'started' ? (
          <div style={{ background: 'rgba(41,121,255,0.06)', border: '1px solid rgba(41,121,255,0.25)', padding: 14, textAlign: 'center' }}>
            <div style={{ width: 20, height: 20, border: '2px solid #2979FF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 8px' }} />
            <p style={{ margin: 0, fontSize: 13, color: '#2979FF', fontWeight: 600 }}>Veriff is processing your documents</p>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>This page will update automatically. Usually takes 1–3 minutes.</p>
          </div>
        ) : null}

        {isVerified && (
          <div style={{ background: 'rgba(0,255,148,0.06)', border: '1px solid rgba(0,255,148,0.3)', padding: 20, textAlign: 'center' }}>
            <p style={{ margin: '0 0 6px', fontSize: 32 }}>✅</p>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#00FF94' }}>Identity Verified</p>
            {status?.verified_name && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--pp-text-3)' }}>{status.verified_name}</p>}
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Verified on {status?.approved_at?.slice(0, 10) || ''}</p>
          </div>
        )}

        <p style={{ margin: '20px 0 0', fontSize: 11, color: 'var(--pp-text-3)', textAlign: 'center', lineHeight: 1.6 }}>
          Powered by <strong>Veriff</strong> · Your data is processed securely and never stored by Profit Pulse.
          <br />Questions? <a href="mailto:support@profit-pulse.pro" style={{ color: 'var(--pp-accent)' }}>support@profit-pulse.pro</a>
        </p>
      </div>
    </div>
  );
}
